import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "~/integrations/supabase/client";
import type { BatchPallet, Pallet } from "~/types/wms";

const PALLETS_KEY = "batch_pallets";
const PRODUCTION_BATCHES_KEY = "production_batches";

/**
 * Get all pallets for a production batch
 */
export function useBatchPallets(batchId: string | undefined) {
  return useQuery({
    queryKey: [PALLETS_KEY, batchId],
    queryFn: async (): Promise<BatchPallet[]> => {
      if (!batchId) return [];

      const { data, error } = await supabase.rpc("get_batch_pallets", {
        p_batch_id: batchId,
      });

      if (error) throw error;
      return data || [];
    },
    enabled: !!batchId,
  });
}

/**
 * Start production of a new pallet
 */
export function useStartPalletProduction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (batchId: string): Promise<Pallet> => {
      const { data, error } = await supabase.rpc("start_pallet_production", {
        p_batch_id: batchId,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: async (_, batchId) => {
      await queryClient.refetchQueries({ queryKey: [PALLETS_KEY, batchId] });
      await queryClient.refetchQueries({
        queryKey: [PRODUCTION_BATCHES_KEY, batchId],
      });
    },
  });
}

/**
 * End production of a pallet
 */
export function useEndPalletProduction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      palletId,
      boxCount,
    }: {
      palletId: string;
      boxCount: number;
    }): Promise<Pallet> => {
      const { data, error } = await supabase.rpc("end_pallet_production", {
        p_pallet_id: palletId,
        p_box_count: boxCount,
      });

      if (error) throw error;
      return data;
    },
    onSuccess: async (pallet) => {
      await queryClient.refetchQueries({
        queryKey: [PALLETS_KEY, pallet.production_batch_id],
      });
      await queryClient.refetchQueries({
        queryKey: [PRODUCTION_BATCHES_KEY, pallet.production_batch_id],
      });
    },
  });
}

/**
 * Mark pallet sticker as printed
 */
export function useMarkStickerPrinted() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (palletId: string): Promise<Pallet> => {
      const { data, error } = await supabase.rpc(
        "mark_pallet_sticker_printed",
        {
          p_pallet_id: palletId,
        }
      );

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PALLETS_KEY] });
    },
  });
}

/**
 * Update a pallet's box count and recalculate batch actual_quantity
 */
export function useUpdatePallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      palletId,
      boxCount,
    }: {
      palletId: string;
      boxCount: number;
    }) => {
      // Update the pallet
      const { data: pallet, error } = await supabase
        .from("wms_pallets")
        .update({
          initial_box_count: boxCount,
          current_box_count: boxCount,
          updated_at: new Date().toISOString(),
        })
        .eq("id", palletId)
        .select("*, production_batch_id")
        .single();

      if (error) throw error;

      // Recalculate batch actual_quantity from all completed pallets
      const { data: palletSum } = await supabase
        .from("wms_pallets")
        .select("initial_box_count")
        .eq("production_batch_id", pallet.production_batch_id)
        .not("production_ended_at", "is", null);

      const totalBoxes = (palletSum || []).reduce(
        (sum, p) => sum + (p.initial_box_count || 0),
        0
      );

      // Update the batch
      await supabase
        .from("production_batches")
        .update({ actual_quantity: totalBoxes, updated_at: new Date().toISOString() })
        .eq("id", pallet.production_batch_id);

      return pallet;
    },
    onSuccess: async (pallet) => {
      await queryClient.refetchQueries({
        queryKey: [PALLETS_KEY, pallet.production_batch_id],
      });
      await queryClient.refetchQueries({
        queryKey: [PRODUCTION_BATCHES_KEY, pallet.production_batch_id],
      });
    },
  });
}

/**
 * Delete a pallet (only if not yet in warehouse)
 */
export function useDeletePallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      palletId,
      batchId,
    }: {
      palletId: string;
      batchId: string;
    }) => {
      const { error } = await supabase
        .from("wms_pallets")
        .delete()
        .eq("id", palletId);

      if (error) throw error;
      return { batchId };
    },
    onSuccess: async (result) => {
      await queryClient.refetchQueries({
        queryKey: [PALLETS_KEY, result.batchId],
      });
      await queryClient.refetchQueries({
        queryKey: [PRODUCTION_BATCHES_KEY, result.batchId],
      });
    },
  });
}

/**
 * Get active pallet (in_production status) for a batch
 */
export function useActivePallet(batchId: string | undefined) {
  return useQuery({
    queryKey: [PALLETS_KEY, batchId, "active"],
    queryFn: async (): Promise<Pallet | null> => {
      if (!batchId) return null;

      // A batch should only ever have one in_production pallet (enforced by the
      // ux_wms_pallets_one_active_per_batch unique index + start_pallet_production
      // guard). We still order + limit(1) so that if a stale duplicate ever slips
      // in, .maybeSingle() does not throw and blank out the finalize UI.
      const { data, error } = await supabase
        .from("wms_pallets")
        .select("*, produced_by_profile:profiles!produced_by(name, lastname)")
        .eq("production_batch_id", batchId)
        .eq("status", "in_production")
        .order("production_started_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!batchId,
  });
}
